import { useEVaultPositions } from '@/domain/euler/positions/useEVaultPositions'
import { SearchInputEuler } from '@/ui/atoms/search-input/SearchInputEuler'
import { usePrivy } from '@privy-io/react-auth'
import { useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { GuestView } from '@/ui/organisms/guest-view/GuestView'
import { EVaultListView } from './EVaultListView'
import { EVaultsSkeleton } from './skeleton/EVaultsSkeleton'
import { useEVaults } from '../logic/useEVaults'

export interface EVaultPositionsProps {
  showInWalletOnly?: boolean
  showFilters?: boolean
}

export function EVaultPositions({ showInWalletOnly = false, showFilters = false }: EVaultPositionsProps) {
  const { address, isConnecting } = useAccount()
  const { connectWallet } = usePrivy()
  const { vaults: marketVaults, isLoading, error } = useEVaults(address)
  const { positions: earnPositions, isLoading: isEarnPositionsLoading } = useEVaultPositions({
    userAddress: address,
    enabled: !!address,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const positionVaultAddresses = useMemo(
    () => new Set(earnPositions.map((position) => position.vaultAddress.toLowerCase())),
    [earnPositions],
  )

  const vaults = useMemo(() => {
    const positionsByVault = new Map(earnPositions.map((position) => [position.vaultAddress.toLowerCase(), position]))

    return marketVaults.map((vault) => {
      const position = positionsByVault.get(vault.address.toLowerCase())

      if (!position) {
        return {
          ...vault,
          userBalance: undefined,
          userBalanceUsd: undefined,
          token: undefined,
        }
      }

      return {
        ...vault,
        userBalanceUsd: position.depositAmountUSD,
        token: position.token,
      }
    })
  }, [marketVaults, earnPositions])
  const hasPortfolioPositions = earnPositions.length > 0

  // Show skeleton while connecting wallet or loading vaults
  if (isConnecting || (address && (isLoading || (isEarnPositionsLoading && !hasPortfolioPositions)))) {
    return <EVaultsSkeleton />
  }

  // Show connect wallet panel if not connected
  if (!address) {
    return <GuestView openConnectModal={connectWallet} />
  }

  if (error) {
    return <div className="text-destructive">Error loading positions: {error.message}</div>
  }

  // Rows come from the vault market hook for TVL/APY metadata, with shared portfolio positions overlaid.
  const filteredVaults = vaults.filter((vault) => {
    if (!positionVaultAddresses.has(vault.address.toLowerCase())) return false

    const assetSymbol = vault.assetSymbol || vault.symbol
    const matchesSearch =
      vault.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vault.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assetSymbol.toLowerCase().includes(searchQuery.toLowerCase())

    // For "In wallet" toggle, filter by walletBalance (underlying asset in wallet)
    const matchesWallet = !showInWalletOnly || (vault.walletBalance && Number(vault.walletBalance) > 0)

    return matchesSearch && matchesWallet
  })

  return (
    <div className="flex flex-col gap-4">
      {/* Filter tabs and search */}
      {showFilters && (
        <div className="flex justify-end">
          <div className="flex items-center gap-4">
            <SearchInputEuler
              placeholder="Search vaults..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {filteredVaults.length === 0 ? (
        <div className="text-muted-foreground">You don&apos;t have open positions yet.</div>
      ) : (
        <EVaultListView vaults={filteredVaults} />
      )}
    </div>
  )
}
